/*
	copyright 2019 sg
*/

import {Feature} from 'ol';
import {Polygon} from 'ol/geom';
import distance from '@turf/distance';

import {calculateLength} from './length';
import transform from './transform';

// Nimmt die Ecken des Features entgegen und gibt die Gesamtstrecke in KM zurueck
// Das ist völlig unabhängig von FAI oder nicht FAI

function calcLength(coords) {
    // coords ist ein Arry mit allen Eckpunkten des Features in der map Prjektion 'EPSG:4326'

    return calculateLength(coords);
}


/*
Berechnet aus den Ecken des Features, ob ein FAI mit drei dieser Ecken moeglich ist
Rueckgabe ist ein Objekt mit den berechneten Werten zum FAI Dreieck:

	faiCalcObj = {
		isFai: boolean,
		faiLengthKm: int,
		amountCombinations: int,
		faiCoords: Array [
			[point1Lon, point1Lat],
			[point2Lon, point2Lat],
			[point3Lon, point3Lat]
		],
		allCoords: Array [
			Array [
				[point1Lon, point1Lat],
				[point2Lon, point2Lat],
				[point3Lon, point3Lat]
			],
			Array [
				[point1Lon, point1Lat],
				[point2Lon, point2Lat],
				[point4Lon, point4Lat]
			],
			Array [
				[point1Lon, point1Lat],
				[point2Lon, point2Lat],
				[point5Lon, point5Lat]
			],
			...
		]
	}

Bei 6 Punkten gibt es bspw. 20 Kombinationen und daher 20 Arrays mit je einer Kombination aus drei Punkten (3 Koordinatenarrays).

Wenn es kein FAI Dreieck ist, dann sieht das Object so aus:
	faiCalcObj = {
		isFai: false,
		faiLengthKm: 0,
	}

So werden die Koordinaten des FAI Dreiecks erzeugt:
	function calcFai(Koordinatenarray des Features: Array)

*/
function calcFai(coords) {

    // Das Rueckgabe-Objekt
    const faiCalcObj = {
        isFai: false,
        faiLengthKm: 0,
    };

    // Wenn es zu viele Punkte sind, dann wird die Berechnung nicht durchgefuehrt (10 Punkte sind bereits 120 moegliche Kombinationen)
    if (coords.length > 10) {
        return faiCalcObj;
    }

    const arrCombinations = getCombinations(coords); // Berechne die Kombinationen aus jeweils drei Eckpunkten
    faiCalcObj.amountCombinations = arrCombinations.length; // Die Anzahl der Kombinationen
    faiCalcObj.allCoords = arrCombinations; // Der Array mit den Kombinationsdetails (s.o.)

    for (let i = 0; i < faiCalcObj.amountCombinations; i++) {
        const myObj = isFaiTriangle(faiCalcObj.allCoords[i]);

        if (myObj.isFai && myObj.faiKms > faiCalcObj.faiLengthKm) {
            faiCalcObj.isFai = true;
            faiCalcObj.faiLengthKm = myObj.faiKms;
            faiCalcObj.faiCoords = myObj.faiCoords;
        }
    }

    return faiCalcObj;
}


function getCombinations(coords) {

    const myResultArray = [];
    coords.pop();

    //console.log(coords);

    //coords = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    const combinations = [];
    const amountSubSets = 3;


    function run(level, start) {

        for (let i = start; i < coords.length - amountSubSets + level + 1; i++) {
            combinations[level] = coords[i];

            if (level < amountSubSets - 1) {
                run(level + 1, i + 1);
            } else {
                myResultArray.push([combinations[0], combinations[1], combinations[2]]);
            }
        }

    }

    run(0, 0);

//    console.log('myResultArray');
//    console.log(myResultArray);

    return myResultArray;
}


// Transformiere einen Array mit Koordinaten in eine andere Projektion
const transformCoordsArray = coords => coords.map(transform);


const THE_25_PERCENT = 0.25;
const THE_28_PERCENT = 0.28;
const THE_45_PERCENT = 0.45;
const THE_500_KM = 500;

function isFaiTriangle(coords) {
    const coordsInLonLat = transformCoordsArray(coords);

    const myFaiTriangle = {
        isFai: false,
        faiKms: 0,
        kms: 0,
        faiCoords: []
    };

    const pointA = coordsInLonLat[0];
    const pointB = coordsInLonLat[1];
    const pointC = coordsInLonLat[2];

    const leg1Dist = distance(pointA, pointB);
    const leg2Dist = distance(pointB, pointC);
    const leg3Dist = distance(pointC, pointA);

    const myLegsDistArray = [leg1Dist, leg2Dist, leg3Dist];

    const minLeg = Math.min(...myLegsDistArray);
    const maxLeg = Math.max(...myLegsDistArray);

    let arrSum = 0;

    for (let i = 0; i < myLegsDistArray.length; i++) {
        arrSum += myLegsDistArray[i];
    }

    if (arrSum < THE_500_KM) {
        if (minLeg >= THE_28_PERCENT * arrSum) {
            myFaiTriangle.isFai = true;
            myFaiTriangle.faiKms = arrSum;
            myFaiTriangle.faiCoords = coords;
        } else {
            myFaiTriangle.isFai = false;
            myFaiTriangle.kms = arrSum;
        }
    } else {
        if (minLeg >= THE_25_PERCENT * arrSum && maxLeg <= THE_45_PERCENT * arrSum) {
            myFaiTriangle.isFai = true;
            myFaiTriangle.faiKms = arrSum;
            myFaiTriangle.faiCoords = coords;
        } else {
            myFaiTriangle.isFai = false;
            myFaiTriangle.kms = arrSum;
        }
    }

    return myFaiTriangle;
}

export const calcPaintFaiTriangle = (faiVectorSource, coords) => {
    const myFaiData = calcFai(coords);

    if (myFaiData.isFai) {
        faiVectorSource.addFeature(new Feature({
            geometry: new Polygon([myFaiData.faiCoords]),
        }));

        return [myFaiData.faiLengthKm.toFixed(0), myFaiData.faiCoords];
    }

    return [0, null];
};
